import React from 'react';
import PropTypes from 'prop-types';
import { Map } from 'react-leaflet';
import { latLngBounds } from 'leaflet';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { connect } from 'react-redux';
import FlightMiniTracker from '../FlightMiniTracker/FlightMiniTracker';
import MapFlick from '../../../../../../../../../../src/components/MapFlick/MapFlick';
import MapSatLayer from '../../../../../../../../../../src/components/MapControlsAndLayers/SatTileLayer';
import MapLabelsLayer from '../../../../../../../../../../src/components/MapControlsAndLayers/LabelsTileLayer';
import MapTerrainLayer from '../../../../../../../../../../src/components/MapControlsAndLayers/TerrainTileLayer';
import MapRadarLayer from '../../../../../../../../../../src/components/MapControlsAndLayers/RadarTileLayer';
import MapZoomControl from '../../../../../../../../../../src/components/MapControlsAndLayers/ZoomControl';
import MapFullScreenControl from '../../../../../../../../../../src/components/MapControlsAndLayers/FullScreenControl';
import FlightMapNote from './children/FlightMapNote';
import componentBase from '../../../../../../../../../../src/lib/component-base';
import BannerAd from '../../../../../../../../../../shared/components/BannerAd/BannerAd';
import { FullScreenMap as fullScreenMapAds } from '../../../../../../../../../FlightTracker/components/ads';
import reportException from '../../../../../../../../../../shared/lib/report-exception/index';

const fullScreenMapAdsId = fullScreenMapAds.id;

const FLICK_MESSAGE_CODE = 'FLICK';
const maxBounds = latLngBounds([90, -300], [-90, 300]);
let mapElement = null;

@connect(state => ({
  flickError: state.flick.error,
}))
@componentBase('FlightMap')
export default class FlightMap extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func,
    flight: PropTypes.object.isRequired,
    flickError: PropTypes.string,
    params: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);
    this.noteWasClosed = this.noteWasClosed.bind(this);

    const initialState = this.getNoteState(this.props);
    this.state = initialState;

    this.fullScreenAds = null;

    this.setRef = this.setRef.bind(this);
    this.mapEnterFullscreen = this.mapEnterFullscreen.bind(this);
    this.mapExitFullscreen = this.mapExitFullscreen.bind(this);
    this.handleFullScreenEvent = this.handleFullScreenEvent.bind(this);
  }

  componentDidMount() {
    if (document.addEventListener) {
      document.addEventListener('webkitfullscreenchange', this.handleFullScreenEvent, false);
      document.addEventListener('mozfullscreenchange', this.handleFullScreenEvent, false);
      document.addEventListener('fullscreenchange', this.handleFullScreenEvent, false);
      document.addEventListener('MSFullscreenChange', this.handleFullScreenEvent, false);
    }
  }

  componentWillReceiveProps(nextProps) {
    const oldNote = this.state.note;
    let newState = this.getNoteState(nextProps);
    const newNote = newState.note;


    if (oldNote && !newNote) {
      // The note went away somehow.
      const code = oldNote.messageCode && oldNote.messageCode.toUpperCase();
      if (['B', 'PNA', FLICK_MESSAGE_CODE].includes(code)) {
        // old note message was Beyond blah blah or Cannot access positions
        // Show Resumed tracking message unless flight is in final state now

        this.logger('\n\nTracking resumed\n\n');

        const note = {
          momentary: true,
          message: 'Re-established position data',
        };
        newState = { note };
      } else {
        const flightNote = nextProps.flight.flightNote;
        if (flightNote && flightNote.tracking) {
          this.logger('\n\nTracking began\n\n');

          const note = {
            momentary: true,
            message: 'The flight is now tracking',
          };
          newState = { note };
        }
      }
    }

    this.setState(newState);
  }

  getNoteState(props) {
    let newState = {};
    const note = this.getNote(props);

    const shouldShowNote = (
      note &&
      !note.tracking &&
      note.message &&
      note.message.length > 0);

    if (shouldShowNote) {
      newState = { note };
    } else {
      newState = { note: null };
    }

    return newState;
  }

  getNote(props = this.props) {
    let note = null;

    if (props.flickError) {
      note = {
        message: props.flickError,
        messageCode: FLICK_MESSAGE_CODE,
        tracking: false,
      };
    }

    if (!note) {
      note = props.flight.flightNote;
    }

    if (note &&
      this.state &&
      this.state.dismissedNote &&
      note.message === this.state.dismissedNote.message) {
      note = null;
    }

    return note;
  }

  setRef = (ref) => {
    if (!this.fullScreenAds && ref) {
      this.fullScreenAds = ref.getWrappedInstance();
    }
  }

  handleFullScreenEvent = () => {
    try {
      const fullScreenTracker = (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      if (!fullScreenTracker) {
        // exit map, close ads only if map is already full screen
        this.mapExitFullscreen();
      }
    } catch (err) {
      const pathName = this.context && this.context.location && this.context.location.pathname;
      reportException(err, pathName);
    }
  }

  noteWasClosed() {
    if (this.state.note) {
      this.setState({
        note: null,
        dismissedNote: this.state.note,
      });
    }
  }

  mapEnterFullscreen() {
    if (this.fullScreenAds) {
      // handle ad showing up
      this.fullScreenAds.defineAndEnableAd();
      this.fullScreenAds.displayAd(fullScreenMapAdsId);
    }
    if (mapElement.requestFullscreen) {
      mapElement.requestFullscreen();
    } else if (mapElement.mozRequestFullScreen) {
      mapElement.mozRequestFullScreen();
    } else if (mapElement.webkitRequestFullscreen) {
      mapElement.webkitRequestFullscreen();
    } else if (mapElement.msRequestFullscreen) {
      mapElement.msRequestFullscreen();
    }
  }

  mapExitFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }

    if (this.fullScreenAds) {
      this.fullScreenAds.destroySlot();
    }
  }

  render() {
    const {
      flight,
    } = this.props;

    let hideControls = true;
    const position = [45.5197, -122.6768];
    let flightMapNote = null;
    let miniTracker = null;

    if (this.state.note) {
      flightMapNote = (
        <FlightMapNote
          key={`flight-map-note-${this.state.note.message}`}
          note={this.state.note}
          onClose={this.noteWasClosed}
          style={{ position: 'relative' }}
        />
      );
    } else {
      hideControls = false;

      const statusCode = flight && flight.status && flight.status.statusCode;

      if (flight && statusCode && statusCode !== 'S' && statusCode !== 'C' && statusCode !== 'U') {
        miniTracker = (<FlightMiniTracker {...this.props} />);
      }
    }

    let hideControlsForMobile = hideControls;

    if (this.context.mediaBreakpoint.isMobile) {
      hideControlsForMobile = true;
    }

    const subscriptionActive = (
      this.context &&
      this.context.currentUser &&
      this.context.currentUser.subscriptionActive
    );

    const adUnits = {
      desktopAd: fullScreenMapAds,
    };

    return (
      <div
        id='flightTrackerMap'
        ref={(mapDiv) => { mapElement = mapDiv; }}
        style={{ width: '100%', height: '100%', position: 'relative' }}
      >
        <div style={{ width: '100%', height: '100%' }}>
          <ReactCSSTransitionGroup transitionName='fade' transitionEnterTimeout={200} transitionLeaveTimeout={200}>
            {flightMapNote}
          </ReactCSSTransitionGroup>
          <Map
            zoomControl={false}
            center={position}
            zoom={7}
            worldCopyJump={false}
            minZoom={2}
            maxZoom={10}
            maxBounds={maxBounds}
            scrollWheelZoom={false}
            style={{ zIndex: 1 }}
          >
            <MapFlick
              flightId={flight.flightId}
              dispatch={this.props.dispatch}
              params={this.props.params}
              disableRefresh={flight.isLanded && !flight.isTracking}
            />
            <MapSatLayer />
            <MapLabelsLayer hide={hideControls} />
            <MapTerrainLayer hide={hideControls} />
            <MapRadarLayer hide={hideControls} />
            <MapFullScreenControl
              hide={hideControlsForMobile}
              enterFullscreen={this.mapEnterFullscreen}
              exitFullscreen={this.mapExitFullscreen}
            />
            <MapZoomControl hide={hideControlsForMobile} />
          </Map>
        </div>
        <BannerAd
          noAdOnLoad
          {...adUnits}
          setRef={this.setRef}
          subscriptionActive={subscriptionActive}
          style={{
            left: '50%',
            position: 'absolute',
            transform: 'translate(-50%,0)',
            top: '0',
            zIndex: 10,
          }}
        />
        <div
          className='row'
          style={{
 position: 'absolute', bottom: '0', width: '100%', zIndex: 10,
}}
        >
          {miniTracker}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/SingleFlight/children/FlightTracker/children/FlightPositionalInformationSection/children/FlightPositionalInformation/children/FlightMap/FlightMap.js